import { keepPreviousData, useQuery } from '@tanstack/react-query'

import { apiFetch } from '@/lib/api'

export type AdminSessionUser = {
  id: string
  name: string
  email: string
  position?: {
    id: string
    name: string
    sortOrder: number
  } | null
}

export type AdminSessionSource =
  | 'EMPLOYEE_APP'
  | 'ADMIN_ADDED'
  | 'ADMIN_CORRECTION'

export interface AdminWorkSession {
  id: string
  userId: string
  user: AdminSessionUser
  startedAt: string
  endedAt: string | null
  startedLat: number | null
  startedLng: number | null
  startedAccuracyM: number | null
  endedLat: number | null
  endedLng: number | null
  endedAccuracyM: number | null
  startedSource: AdminSessionSource
  endedSource: AdminSessionSource | null
  clientEventId: string | null
  endedClientEventId: string | null
  autoClosed: boolean
  autoClosedAt: string | null
  autoClosedEndedAt: string | null
  /** null przy autoClosed = pracownik jeszcze nie podał właściwej godziny wyjścia. */
  autoCloseResolvedAt: string | null
  /**
   * M8 — godzina kliknięcia „Koniec pracy" oddanego daleko od placówki.
   * Nie-null znaczy, że o tę godzinę zapytaliśmy pracownika.
   */
  remoteEndPromptAt: string | null
  /** Dystans od pinezki w chwili tego kliknięcia (metry), zamrożony. */
  remoteEndPromptDistanceM: number | null
  /**
   * null przy ustawionym `remoteEndPromptAt` = pytanie wciąż wisi.
   * Po END_PROMPT_WINDOW_DAYS pracownik nie dostanie już modala — od tego
   * momentu jedyną osobą, która to zobaczy, jest admin w widoku dnia.
   */
  remoteEndResolvedAt: string | null
  outsideClinic: boolean
  startedOutside: boolean
  startedDistanceM: number | null
  endedOutside: boolean
  endedDistanceM: number | null
  notes: string | null
  durationSeconds: number | null
  createdAt: string
  updatedAt: string
}

export interface AdminSessionsParams {
  from: string
  to: string
  userId?: string
  autoClosed?: boolean
}

const ADMIN_SESSIONS_KEY = ['admin-sessions'] as const

export interface UseAdminSessionsOptions {
  /**
   * Trzyma poprzedni wynik na ekranie podczas pobierania nowego zakresu.
   * Kalendarz przełącza tygodnie strzałkami — bez tego cały widok znikał
   * i wracał spinnerem przy każdym kliknięciu.
   */
  keepPrevious?: boolean
  /**
   * Odpytywanie w tle. Kalendarz bieżącego tygodnia jest ekranem podglądu
   * na żywo — bez tego otwarta sesja i świeżo zamknięte wyjście nie pojawiały
   * się aż do przeładowania (globalny `refetchOnWindowFocus` jest wyłączony,
   * a iOS PWA po wznowieniu z tła nie przeładowuje dokumentu).
   * Historyczne tygodnie odpytujemy bez sensu, więc to opcja, nie domyślka.
   */
  refetchIntervalMs?: number
}

export function useAdminSessions(
  params: AdminSessionsParams,
  options?: UseAdminSessionsOptions,
) {
  return useQuery({
    ...(options?.keepPrevious ? { placeholderData: keepPreviousData } : {}),
    ...(options?.refetchIntervalMs
      ? { refetchInterval: options.refetchIntervalMs }
      : {}),
    queryKey: [...ADMIN_SESSIONS_KEY, 'list', params],
    queryFn: () => {
      const qs = new URLSearchParams()
      qs.set('from', params.from)
      qs.set('to', params.to)
      if (params.userId) qs.set('userId', params.userId)
      if (params.autoClosed !== undefined) {
        qs.set('autoClosed', String(params.autoClosed))
      }
      return apiFetch<{ sessions: AdminWorkSession[] }>(
        `/api/admin/work-sessions?${qs.toString()}`,
      )
    },
    staleTime: 60_000,
  })
}
